import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@/components/ui/input-otp";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Shield, Smartphone, Copy, Check, ChevronDown, Loader2, AlertCircle } from "lucide-react";
import { logger } from "@/lib/logger";
import { useMFA } from "@/contexts/MFAContext";

interface EnrollMFAProps {
  onEnrolled: () => void;
  onCancel?: () => void;
}

// Session storage key for persisting enrollment state
const MFA_ENROLLMENT_KEY = "mfa_enrollment_state";

interface StoredEnrollmentState {
  factorId: string;
  qrCode: string;
  secret: string;
  friendlyName: string;
  timestamp: number;
}

// Module-level cache to survive component remounts (but not page reloads)
// This prevents re-enrollment when React remounts the component
let moduleEnrollmentCache: StoredEnrollmentState | null = null;

// Helper to get stored enrollment state
function getStoredEnrollment(): StoredEnrollmentState | null {
  // Check module cache first (survives remounts but not page reloads)
  if (moduleEnrollmentCache) {
    const age = Date.now() - moduleEnrollmentCache.timestamp;
    logger.debug("EnrollMFA: Found module cache, factorId:", moduleEnrollmentCache.factorId, "age:", age, "ms");

    const TEN_MINUTES = 10 * 60 * 1000;
    if (age < TEN_MINUTES) {
      return moduleEnrollmentCache;
    } else {
      logger.debug("EnrollMFA: Module cache expired, clearing");
      moduleEnrollmentCache = null;
    }
  }

  // Fall back to sessionStorage (survives page reloads)
  try {
    const stored = sessionStorage.getItem(MFA_ENROLLMENT_KEY);
    logger.debug("EnrollMFA: sessionStorage raw value:", stored ? "exists" : "null");
    if (!stored) return null;

    const state = JSON.parse(stored) as StoredEnrollmentState;
    logger.debug("EnrollMFA: Parsed stored state, factorId:", state.factorId, "age:", Date.now() - state.timestamp, "ms");

    // Expire after 10 minutes (TOTP enrollment shouldn't take longer)
    const TEN_MINUTES = 10 * 60 * 1000;
    if (Date.now() - state.timestamp > TEN_MINUTES) {
      logger.debug("EnrollMFA: Stored state expired, clearing");
      sessionStorage.removeItem(MFA_ENROLLMENT_KEY);
      return null;
    }

    // Populate module cache from sessionStorage
    moduleEnrollmentCache = state;
    return state;
  } catch (err) {
    logger.error("EnrollMFA: Error reading stored enrollment:", err);
    return null;
  }
}

// Helper to store enrollment state
function storeEnrollment(state: Omit<StoredEnrollmentState, "timestamp">) {
  const toStore = { ...state, timestamp: Date.now() };

  // Update module cache (survives remounts)
  moduleEnrollmentCache = toStore;
  logger.debug("EnrollMFA: Updated module cache for factorId:", state.factorId);

  // Also persist to sessionStorage (survives page reloads)
  try {
    sessionStorage.setItem(MFA_ENROLLMENT_KEY, JSON.stringify(toStore));
    logger.debug("EnrollMFA: Stored enrollment in sessionStorage for factorId:", state.factorId);
  } catch (err) {
    logger.error("EnrollMFA: Failed to store in sessionStorage:", err);
  }
}

// Helper to clear enrollment state
function clearStoredEnrollment() {
  // Clear module cache
  moduleEnrollmentCache = null;

  // Clear sessionStorage
  try {
    sessionStorage.removeItem(MFA_ENROLLMENT_KEY);
  } catch {
    // sessionStorage might not be available
  }
  logger.debug("EnrollMFA: Cleared enrollment state from cache and storage");
}

// Debug: Log when module loads (indicates full page reload)
const MODULE_LOAD_TIME = Date.now();
logger.debug("EnrollMFA: Module loaded at", MODULE_LOAD_TIME);

export function EnrollMFA({ onEnrolled, onCancel }: EnrollMFAProps) {
  const { refreshMFAStatus } = useMFA();

  // Debug: Log component mount time
  logger.debug("EnrollMFA: Component rendering, module loaded", Date.now() - MODULE_LOAD_TIME, "ms ago");

  // Check for cached enrollment immediately on render (before useState)
  // This allows us to initialize state with cached values
  const cachedEnrollment = getStoredEnrollment();

  // Enrollment step: "name" -> "scan" -> "verify"
  // If we have cached enrollment, skip to "scan" step
  const [step, setStep] = useState<"name" | "scan" | "verify">(cachedEnrollment ? "scan" : "name");
  const [friendlyName, setFriendlyName] = useState<string>(cachedEnrollment?.friendlyName ?? "");
  const [factorId, setFactorId] = useState<string>(cachedEnrollment?.factorId ?? "");
  const [qrCode, setQrCode] = useState<string>(cachedEnrollment?.qrCode ?? "");
  const [secret, setSecret] = useState<string>(cachedEnrollment?.secret ?? "");
  const [verifyCode, setVerifyCode] = useState<string>("");
  const [error, setError] = useState<string>("");
  // If we have cached enrollment, don't show loading
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [secretCopied, setSecretCopied] = useState<boolean>(false);
  const [showManualEntry, setShowManualEntry] = useState<boolean>(false);

  // Ref to prevent double-enrollment in React StrictMode
  const enrollmentStarted = useRef(!!cachedEnrollment); // Already started if we have cache

  // Verify cached enrollment is still valid on mount
  useEffect(() => {
    const verifyCachedEnrollment = async () => {
      // If we have cached enrollment, verify factor still exists
      if (cachedEnrollment && factorId && qrCode) {
        logger.debug("EnrollMFA: Verifying cached enrollment, factorId:", factorId);
        const { data } = await supabase.auth.mfa.listFactors();
        const stillExists = data?.all?.find(
          factor =>
            factor.factor_type === 'totp' &&
            factor.id === factorId &&
            factor.status === 'unverified'
        );
        if (!stillExists) {
          logger.debug("EnrollMFA: Cached factor no longer valid, resetting to naming step");
          clearStoredEnrollment();
          enrollmentStarted.current = false;
          setFactorId("");
          setQrCode("");
          setSecret("");
          setFriendlyName("");
          setStep("name");
        }
      }
    };
    verifyCachedEnrollment();
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start enrollment with user-provided name
  const startEnrollment = async (customName: string) => {
    // Prevent double-enrollment
    if (enrollmentStarted.current) {
      logger.debug("EnrollMFA: Enrollment already started, skipping");
      return;
    }
    enrollmentStarted.current = true;

    setIsLoading(true);
    setError("");

    try {
      logger.debug("EnrollMFA: Starting fresh TOTP enrollment with name:", customName);

      // Get current factors and clean up any stale unverified ones
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();

      if (factorsError) {
        logger.error("EnrollMFA: Error listing factors:", factorsError);
        throw factorsError;
      }

      // Remove any existing unverified factors before creating a new one
      const unverifiedFactors =
        factorsData.all?.filter(
          factor =>
            factor.factor_type === 'totp' && factor.status === 'unverified'
        ) || [];
      if (unverifiedFactors.length > 0) {
        logger.debug("EnrollMFA: Removing", unverifiedFactors.length, "stale unverified factor(s)");
        for (const factor of unverifiedFactors) {
          logger.debug("EnrollMFA: Removing unverified factor:", factor.id);
          const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
          if (unenrollError) {
            logger.error("EnrollMFA: Failed to unenroll factor:", factor.id, unenrollError);
            // Continue anyway - the factor might already be gone
          }
        }
      }

      // Check if the custom name is already in use
      const verifiedFactors = factorsData.totp?.filter(f => f.status === "verified") || [];
      const existingNames = verifiedFactors.map(f => f.friendly_name || "");

      let finalName = customName.trim() || "Authenticator App";

      // If the name already exists, append a number
      if (existingNames.includes(finalName)) {
        let counter = 2;
        while (existingNames.includes(`${finalName} ${counter}`)) {
          counter++;
        }
        finalName = `${finalName} ${counter}`;
      }

      logger.debug("EnrollMFA: Using friendly name:", finalName);

      // Create a new enrollment
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: finalName,
      });

        if (enrollError) {
          logger.error("EnrollMFA: Enrollment error:", enrollError);
          throw enrollError;
        }

        if (!data || !data.totp) {
          throw new Error("No TOTP data returned from enrollment");
        }

        logger.debug("EnrollMFA: Enrollment started, factor ID:", data.id);

        // Store the enrollment state so it survives page navigation
      storeEnrollment({
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        friendlyName: finalName,
      });

      setFactorId(data.id);
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFriendlyName(finalName);
      setStep("scan");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to start MFA enrollment";
      setError(message);
      logger.error("EnrollMFA: Failed to start enrollment:", err);
      // Reset the ref so user can retry
      enrollmentStarted.current = false;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle the "Continue" button on the naming step
  const handleNameSubmit = () => {
    startEnrollment(friendlyName);
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    if (!factorId) {
      setError("No factor to verify. Please refresh the page and try again.");
      return;
    }

    setIsVerifying(true);
    setError("");

    try {
      logger.debug("EnrollMFA: Creating challenge for factor:", factorId);

      // Create a challenge
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });

      if (challengeError) {
        logger.error("EnrollMFA: Challenge error:", challengeError);
        throw challengeError;
      }

      logger.debug("EnrollMFA: Challenge created:", challengeData.id);

      // Verify the code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) {
        logger.error("EnrollMFA: Verification error:", verifyError);
        throw verifyError;
      }

      logger.debug("EnrollMFA: MFA enrollment verified successfully");

      // Clear the stored enrollment state since we're done
      clearStoredEnrollment();

      // Refresh MFA status in context
      await refreshMFAStatus();

      // Notify parent
      onEnrolled();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Verification failed";

      // Provide user-friendly error messages
      if (message.includes("Invalid") || message.includes("invalid")) {
        setError("Invalid code. Please check your authenticator app and try again.");
      } else if (message.includes("expired")) {
        setError("Code expired. Please enter the current code from your app.");
      } else {
        setError(message);
      }

      // Clear the code on error so user can retry
      setVerifyCode("");
    } finally {
      setIsVerifying(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = secret;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  // Handle auto-submit when 6 digits entered
  useEffect(() => {
    if (verifyCode.length === 6 && !isVerifying && factorId) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyCode]);

  // Handle cancel - clear stored enrollment and call parent
  const handleCancel = useCallback(() => {
    clearStoredEnrollment();
    onCancel?.();
  }, [onCancel]);

  // Loading state
  if (isLoading) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Setting up two-factor authentication...</p>
        </CardContent>
      </Card>
    );
  }

  // Step 1: Name your authenticator
  if (step === "name") {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-xl">Set Up Two-Factor Authentication</CardTitle>
          <CardDescription>
            Give your authenticator a name to help you identify it later
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="friendlyName">Authenticator Name</Label>
            <Input
              id="friendlyName"
              type="text"
              placeholder="e.g., iPhone, Work Phone, 1Password"
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              This helps you identify which device or app to use when signing in
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button onClick={handleNameSubmit} className="w-full">
              Continue
            </Button>

            {onCancel && (
              <Button variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </div>

          {/* Authenticator App Suggestions */}
          <div className="pt-4 border-t">
            <p className="text-xs text-center text-muted-foreground mb-2">
              Don't have an authenticator app?
            </p>
            <div className="flex justify-center gap-4 text-xs text-muted-foreground">
              <a
                href="https://support.google.com/accounts/answer/1066447"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <Smartphone className="h-3 w-3" />
                Google Authenticator
              </a>
              <a
                href="https://support.1password.com/one-time-passwords/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <Smartphone className="h-3 w-3" />
                1Password
              </a>
              <a
                href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <Smartphone className="h-3 w-3" />
                Microsoft Authenticator
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Step 2 & 3: Scan QR code and verify
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-xl">Set Up Two-Factor Authentication</CardTitle>
        <CardDescription>
          Scan the QR code with your authenticator app to secure your account
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Show authenticator name */}
        {friendlyName && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Setting up: <span className="font-medium text-foreground">{friendlyName}</span>
            </p>
          </div>
        )}

        {/* QR Code */}
        {qrCode && (
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-white p-4 rounded-lg">
              <img
                src={qrCode}
                alt="QR Code for authenticator app"
                className="w-48 h-48"
              />
            </div>

            {/* Manual Entry Option */}
            <Collapsible open={showManualEntry} onOpenChange={setShowManualEntry}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  <ChevronDown
                    className={`h-4 w-4 mr-1 transition-transform ${
                      showManualEntry ? "rotate-180" : ""
                    }`}
                  />
                  Can't scan the code?
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="bg-muted p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-2">
                    Enter this code manually in your authenticator app:
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="bg-background px-3 py-1.5 rounded text-sm font-mono break-all">
                      {secret}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={copySecret}
                    >
                      {secretCopied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Verification Code Input */}
        <div className="space-y-3">
          <p className="text-sm text-center text-muted-foreground">
            Enter the 6-digit code from your authenticator app
          </p>

          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={verifyCode}
              onChange={setVerifyCode}
              disabled={isVerifying || !factorId}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleVerify}
            disabled={verifyCode.length !== 6 || isVerifying || !factorId}
            className="w-full"
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify and Enable"
            )}
          </Button>

          {onCancel && (
            <Button variant="ghost" onClick={handleCancel} disabled={isVerifying}>
              Cancel
            </Button>
          )}
        </div>

        {/* Authenticator App Suggestions */}
        <div className="pt-4 border-t">
          <p className="text-xs text-center text-muted-foreground mb-2">
            Don't have an authenticator app?
          </p>
          <div className="flex justify-center gap-4 text-xs text-muted-foreground">
            <a
              href="https://support.google.com/accounts/answer/1066447"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-foreground transition-colors"
            >
              <Smartphone className="h-3 w-3" />
              Google Authenticator
            </a>
            <a
              href="https://support.1password.com/one-time-passwords/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-foreground transition-colors"
            >
              <Smartphone className="h-3 w-3" />
              1Password
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
